import React, { useMemo, useState } from "react";
import axios from "axios";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { Checkbox } from "../../../components/ui/Checkbox";
import ManualPaymentModal from "./ManualPaymentModal";
import { formatBaseLabel } from "../../../utils/baseDisplay";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "") || "http://localhost:8080";

const BRL = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => BRL.format(Number.isFinite(value) ? value : 0);

const SuppliersTable = ({ items = [], onSupplierClick, onBulkAction, onRefresh }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "amount", direction: "desc" });
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [isPaying, setIsPaying] = useState(false);
  const [payError, setPayError] = useState("");

  const resolveSupplierName = (item) =>
    item?.supplier ||
    item?.fornecedor ||
    item?.supplierName ||
    item?.nomeFornecedor ||
    item?.razaoSocial ||
    item?.nome ||
    "Não informado";

  const resolveCategory = (item) =>
    item?.category ||
    item?.categoria ||
    item?.supplierCategory ||
    item?.grupo ||
    "Não categorizado";

  const resolvePaymentId = (item) => item?.paymentId || item?.codigoPagamento || item?.codigo || item?.id;

  const resolveBaseKey = (item) => item?.base || item?.baseKey || item?.origem || item?.empresa || "ALL";

  const getStatusBadge = (status, daysToDue) => {
    const cfg = {
      paid: { label: "Pago", class: "bg-success/10 text-success", icon: "CheckCircle" },
      canceled: { label: "Cancelado", class: "bg-muted/40 text-muted-foreground", icon: "Ban" },
      overdue: { label: "Vencido", class: "bg-error/10 text-error", icon: "AlertCircle" },
      pending: { label: "Pendente", class: "bg-warning/10 text-warning", icon: "Clock" },
      partial: { label: "Parcial", class: "bg-warning/10 text-warning", icon: "Clock" },
    };

    let resolved = cfg[status] || cfg.pending;

    if ((status === "pending" || status === "partial") && typeof daysToDue === "number" && daysToDue < 0) {
      resolved = cfg.overdue;
    }

    return (
      <span className={`inline-flex items-center space-x-1 text-xs px-2 py-1 rounded-full font-caption ${resolved.class}`}>
        <Icon name={resolved.icon} size={12} />
        <span>{resolved.label}</span>
      </span>
    );
  };

  const getAgingColor = (daysToDue) => {
    if (typeof daysToDue !== "number" || Number.isNaN(daysToDue)) return "text-muted-foreground";
    if (daysToDue < 0) return "text-error font-body-medium";
    if (daysToDue <= 7) return "text-success";
    if (daysToDue <= 15) return "text-warning";
    return "text-muted-foreground";
  };

  const handleSelectAll = (checked) => {
    if (checked) setSelectedItems(items.map((i) => i.id));
    else setSelectedItems([]);
  };

  const handleSelectItem = (itemId, checked) => {
    if (checked) setSelectedItems((prev) => [...prev, itemId]);
    else setSelectedItems((prev) => prev.filter((id) => id !== itemId));
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "ArrowUpDown";
    return sortConfig.direction === "asc" ? "ArrowUp" : "ArrowDown";
  };

  const sortedData = useMemo(() => {
    const copy = [...items];

    return copy.sort((a, b) => {
      const av = a?.[sortConfig.key];
      const bv = b?.[sortConfig.key];

      if (sortConfig.direction === "asc") {
        return av > bv ? 1 : -1;
      }
      return av < bv ? 1 : -1;
    });
  }, [items, sortConfig]);

  const totals = useMemo(() => {
    const totalGeral = items.reduce((s, i) => s + (Number.isFinite(i?.amount) ? i.amount : 0), 0);

    const pendentesItems = items.filter((i) => i?.status !== "paid" && i?.status !== "canceled");
    const pendentes = pendentesItems.reduce((s, i) => s + (Number.isFinite(i?.amount) ? i.amount : 0), 0);

    const vencidas = items
      .filter((i) => i?.status === "overdue")
      .reduce((s, i) => s + (Number.isFinite(i?.amount) ? i.amount : 0), 0);

    return {
      totalGeral,
      pendentes,
      vencidas,
      pendentesCount: pendentesItems.length,
    };
  }, [items]);

  const openDetails = (item) => {
    setSelectedSupplier(item);
    onSupplierClick?.(item);
  };

  const canPay = (item) => item?.status !== "paid" && item?.status !== "canceled";

  const openPayModal = (item) => {
    setPayError("");
    setPayTarget(item);
    setIsPayModalOpen(true);
  };

  const closePayModal = () => {
    if (isPaying) return;
    setIsPayModalOpen(false);
    setPayTarget(null);
    setPayError("");
  };

  const confirmPayment = async (paymentData) => {
    const target = payTarget;
    if (!target) return;

    const codigo = resolvePaymentId(target);
    const base = resolveBaseKey(target);

    if (!codigo) {
      setPayError("Não encontrei o código do pagamento para efetivar.");
      return;
    }

    setIsPaying(true);
    setPayError("");

    try {
      try {
        await axios.post(
          `${API_BASE_URL}/api/pagar/${encodeURIComponent(String(codigo))}/pagar`,
          { base, ...paymentData },
          { headers: { "Content-Type": "application/json" } }
        );
      } catch (_e1) {
        await axios.post(
          `${API_BASE_URL}/api/pagar/efetivar`,
          { codigo, base, ...paymentData },
          { headers: { "Content-Type": "application/json" } }
        );
      }

      setIsPayModalOpen(false);
      setPayTarget(null);
      onRefresh?.();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Falha ao efetivar pagamento. Verifique as logs do back-end.";
      setPayError(msg);
    } finally {
      setIsPaying(false);
    }
  };

  // ✅ AQUI: SEMPRE MOSTRAR POSITIVO (remove o “-” da frente)
  const displayAmount = (item) => {
    const a = Number.isFinite(item?.amount) ? item.amount : 0;
    return Math.abs(a);
  };

  return (
    <div className="bg-card rounded-lg border border-border card-shadow">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading-semibold text-lg text-card-foreground mb-1">Contas a Pagar - Detalhado</h3>
            <p className="text-sm text-muted-foreground">Gestão completa de obrigações com fornecedores</p>
          </div>

          {selectedItems.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">{selectedItems.length} selecionado(s)</span>
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                onClick={() => onBulkAction?.("export", selectedItems)}
              >
                Exportar
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-accent/50 border-b border-border">
            <tr>
              <th className="px-6 py-3 text-left">
                <Checkbox
                  checked={items.length > 0 && selectedItems.length === items.length}
                  indeterminate={selectedItems.length > 0 && selectedItems.length < items.length}
                  onChange={handleSelectAll}
                />
              </th>

              <th
                className="px-6 py-3 text-left text-xs font-body-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-card-foreground"
                onClick={() => handleSort("invoiceNumber")}
              >
                <div className="flex items-center space-x-1">
                  <span>Nota Fiscal</span>
                  <Icon name={getSortIcon("invoiceNumber")} size={14} />
                </div>
              </th>

              <th
                className="px-6 py-3 text-left text-xs font-body-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-card-foreground"
                onClick={() => handleSort("supplier")}
              >
                <div className="flex items-center space-x-1">
                  <span>Fornecedor</span>
                  <Icon name={getSortIcon("supplier")} size={14} />
                </div>
              </th>

              <th
                className="px-6 py-3 text-left text-xs font-body-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-card-foreground"
                onClick={() => handleSort("amount")}
              >
                <div className="flex items-center space-x-1">
                  <span>Valor</span>
                  <Icon name={getSortIcon("amount")} size={14} />
                </div>
              </th>

              <th
                className="px-6 py-3 text-left text-xs font-body-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-card-foreground"
                onClick={() => handleSort("dueDateBR")}
              >
                <div className="flex items-center space-x-1">
                  <span>Vencimento</span>
                  <Icon name={getSortIcon("dueDateBR")} size={14} />
                </div>
              </th>

              <th className="px-6 py-3 text-left text-xs font-body-medium text-muted-foreground uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-body-medium text-muted-foreground uppercase tracking-wider">Aging</th>
              <th className="px-6 py-3 text-left text-xs font-body-medium text-muted-foreground uppercase tracking-wider">Ações</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {sortedData.map((item) => (
              <tr key={item?.id} className="hover:bg-accent/30 transition-colors">
                <td className="px-6 py-4">
                  <Checkbox checked={selectedItems.includes(item?.id)} onChange={(checked) => handleSelectItem(item?.id, checked)} />
                </td>

                <td className="px-6 py-4">
                  <div>
                    <p className="font-body-medium text-sm text-card-foreground">{item?.invoiceNumber || "-"}</p>
                    <p className="text-xs text-muted-foreground">Emissão: {item?.issueDateBR || "-"}</p>
                  </div>
                </td>

                <td className="px-6 py-4">
                  <div>
                    <button
                      className="font-body-medium text-sm text-primary hover:text-primary/80 text-left"
                      onClick={() => openDetails(item)}
                      type="button"
                    >
                      {resolveSupplierName(item)}
                    </button>
                    <p className="text-xs text-muted-foreground">{resolveCategory(item)}</p>
                  </div>
                </td>

                <td className="px-6 py-4">
                  <p className="font-heading-semibold text-sm text-card-foreground">{formatCurrency(displayAmount(item))}</p>
                </td>

                <td className="px-6 py-4">
                  <p className={`text-sm ${getAgingColor(item?.daysToDue)}`}>{item?.dueDateBR || "-"}</p>
                </td>

                <td className="px-6 py-4">{getStatusBadge(item?.status, item?.daysToDue)}</td>

                <td className="px-6 py-4">
                  <span className={`text-sm ${getAgingColor(item?.daysToDue)}`}>
                    {typeof item?.daysToDue === "number" && !Number.isNaN(item?.daysToDue)
                      ? item.daysToDue < 0
                        ? `${Math.abs(item.daysToDue)} dias atraso`
                        : `${item.daysToDue} dias`
                      : "-"}
                  </span>
                </td>

                <td className="px-6 py-4">
                  <Button
                    variant="outline"
                    size="xs"
                    iconName="FileText"
                    iconPosition="left"
                    onClick={() => openDetails(item)}
                    className="text-xs"
                  >
                    Mais detalhes
                  </Button>
                </td>
              </tr>
            ))}

            {sortedData.length === 0 && (
              <tr>
                <td className="px-6 py-10 text-center text-sm text-muted-foreground" colSpan={8}>
                  Sem dados para exibir no período selecionado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedSupplier && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg border border-border bg-card shadow-xl">
            <div className="flex items-center justify-between border-b border-border p-4">
              <div>
                <p className="text-sm text-muted-foreground">Mais detalhes</p>
                <h3 className="font-heading-semibold text-lg text-card-foreground">{resolveSupplierName(selectedSupplier)}</h3>
              </div>

              <button
                type="button"
                className="rounded-md p-2 hover:bg-accent/40"
                onClick={() => setSelectedSupplier(null)}
                aria-label="Fechar"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Nota Fiscal</span>
                <span className="text-card-foreground">{selectedSupplier?.invoiceNumber || "-"}</span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Emissão</span>
                <span className="text-card-foreground">{selectedSupplier?.issueDateBR || "-"}</span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Vencimento</span>
                <span className="text-card-foreground">{selectedSupplier?.dueDateBR || "-"}</span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Valor</span>
                <span className="font-heading-semibold text-card-foreground">
                  {formatCurrency(displayAmount(selectedSupplier))}
                </span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <span>{getStatusBadge(selectedSupplier?.status, selectedSupplier?.daysToDue)}</span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Aging</span>
                <span className={getAgingColor(selectedSupplier?.daysToDue)}>
                  {typeof selectedSupplier?.daysToDue === "number" && !Number.isNaN(selectedSupplier?.daysToDue)
                    ? selectedSupplier.daysToDue < 0
                      ? `${Math.abs(selectedSupplier.daysToDue)} dias atraso`
                      : `${selectedSupplier.daysToDue} dias`
                    : "-"}
                </span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Categoria</span>
                <span className="text-card-foreground">{resolveCategory(selectedSupplier)}</span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Empresa</span>
                <span className="text-card-foreground">{formatBaseLabel(resolveBaseKey(selectedSupplier))}</span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Código do pagamento</span>
                <span className="text-card-foreground">{String(resolvePaymentId(selectedSupplier) || "-")}</span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-border p-4">
              {canPay(selectedSupplier) && (
                <Button
                  variant="primary"
                  size="sm"
                  iconName="CreditCard"
                  iconPosition="left"
                  onClick={() => openPayModal(selectedSupplier)}
                >
                  Pagar
                </Button>
              )}

              <Button variant="outline" size="sm" onClick={() => setSelectedSupplier(null)}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      <ManualPaymentModal
        isOpen={isPayModalOpen}
        onClose={closePayModal}
        payable={payTarget}
        isProcessing={isPaying}
        errorMessage={payError}
        onConfirm={confirmPayment}
        formatCurrency={formatCurrency}
        resolveSupplierName={resolveSupplierName}
      />

      <div className="p-6 border-t border-border bg-accent/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-8">
            <div>
              <p className="text-sm text-muted-foreground">Total Geral</p>
              <p className="font-heading-semibold text-lg text-card-foreground">{formatCurrency(totals.totalGeral)}</p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">Pendente</p>
              <p className="font-heading-semibold text-lg text-warning">{formatCurrency(totals.pendentes)}</p>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">Em Atraso</p>
              <p className="font-heading-semibold text-lg text-error">{formatCurrency(totals.vencidas)}</p>
            </div>
          </div>

          <div className="text-right">
            <p className="text-xs text-muted-foreground mb-1">
              {items.length} faturas • {totals.pendentesCount} pendentes
            </p>

            <div className="flex items-center space-x-2">
              <Button
                variant="default"
                size="sm"
                iconName="Download"
                iconPosition="left"
                onClick={() => onBulkAction?.("export", items.map((i) => i.id))}
              >
                Exportar Tudo
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuppliersTable;
